import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Search, Filter, Phone, Mail, MapPin, Edit2, Trash2, Eye, Plus } from 'lucide-react';
import { getAuthToken } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

interface Stakeholder {
  stakeholderId: string;
  type: string;
  name: string;
  email: string;
  phone: string;
  alternatePhone?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  branchId: string;
  status: string;
  studentDetails?: any;
  parentDetails?: any;
  alumniDetails?: any;
  partnerDetails?: any;
  notes?: string;
  createdAt: any;
}

const StakeholderManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'parents' | 'students' | 'alumni' | 'partners'>('all');
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [filteredStakeholders, setFilteredStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState<Stakeholder | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    byType: { parent: 0, student: 0, alumni: 0, partner: 0 }
  });

  // Form state
  const [formData, setFormData] = useState({
    type: 'parent',
    name: '',
    email: '',
    phone: '',
    alternatePhone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    branchId: 'kolkata',
    notes: '',
    studentDetails: { grade: '', section: '', rollNumber: '' },
    parentDetails: { occupation: '', relationship: '' },
    alumniDetails: { graduationYear: '', currentOccupation: '', company: '' },
    partnerDetails: { organizationType: '', partnershipType: '', contactPerson: '' }
  });

  useEffect(() => {
    fetchStakeholders();
    fetchStats();
  }, []);

  useEffect(() => {
    filterStakeholders();
  }, [stakeholders, activeTab, searchTerm]);

  const fetchStakeholders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/stakeholders`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      setStakeholders(response.data.data || []);
    } catch (error) {
      console.error('Error fetching stakeholders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stakeholders/stats`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterStakeholders = () => {
    let filtered = stakeholders;

    // Filter by type
    if (activeTab !== 'all') {
      const typeMap = {
        parents: 'parent',
        students: 'student',
        alumni: 'alumni',
        partners: 'partner'
      };
      filtered = filtered.filter(s => s.type === typeMap[activeTab as keyof typeof typeMap]);
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(search) ||
        s.email?.toLowerCase().includes(search) ||
        s.phone?.includes(search)
      );
    }

    setFilteredStakeholders(filtered);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API_URL}/stakeholders`, formData, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      alert('Stakeholder created successfully!');
      setShowAddModal(false);
      resetForm();
      fetchStakeholders();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create stakeholder');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (stakeholderId: string) => {
    if (!window.confirm('Are you sure you want to delete this stakeholder?')) return;

    try {
      await axios.delete(`${API_URL}/stakeholders/${stakeholderId}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      alert('Stakeholder deleted successfully!');
      fetchStakeholders();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete stakeholder');
    }
  };

  const handleViewDetails = async (stakeholder: Stakeholder) => {
    try {
      const response = await axios.get(`${API_URL}/stakeholders/${stakeholder.stakeholderId}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      setSelectedStakeholder(response.data.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching stakeholder details:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'parent',
      name: '',
      email: '',
      phone: '',
      alternatePhone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      branchId: 'kolkata',
      notes: '',
      studentDetails: { grade: '', section: '', rollNumber: '' },
      parentDetails: { occupation: '', relationship: '' },
      alumniDetails: { graduationYear: '', currentOccupation: '', company: '' },
      partnerDetails: { organizationType: '', partnershipType: '', contactPerson: '' }
    });
  };

  const getTypeBadge = (type: string) => {
    const badges = {
      parent: 'bg-blue-100 text-blue-800',
      student: 'bg-green-100 text-green-800',
      alumni: 'bg-purple-100 text-purple-800',
      partner: 'bg-orange-100 text-orange-800'
    };
    return badges[type as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stakeholder Management</h1>
          <p className="text-gray-600">Manage parents, students, alumni, and partners</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Stakeholder
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Users className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Parents</p>
              <p className="text-2xl font-bold text-blue-600">{stats.byType.parent}</p>
            </div>
            <Users className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Students</p>
              <p className="text-2xl font-bold text-green-600">{stats.byType.student}</p>
            </div>
            <Users className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Alumni</p>
              <p className="text-2xl font-bold text-purple-600">{stats.byType.alumni}</p>
            </div>
            <Users className="w-8 h-8 text-purple-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Partners</p>
              <p className="text-2xl font-bold text-orange-600">{stats.byType.partner}</p>
            </div>
            <Users className="w-8 h-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['all', 'parents', 'students', 'alumni', 'partners'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Search Bar */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
      </div>

      {/* Stakeholders List */}
      <div className="card">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : filteredStakeholders.length === 0 ? (
          <p className="text-gray-500">No stakeholders found</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStakeholders.map((stakeholder) => (
              <div key={stakeholder.stakeholderId} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{stakeholder.name}</h3>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs mt-1 ${getTypeBadge(stakeholder.type)}`}>
                      {stakeholder.type}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewDetails(stakeholder)}
                      className="text-blue-600 hover:text-blue-800"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(stakeholder.stakeholderId)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  {stakeholder.email && (
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      <span className="truncate">{stakeholder.email}</span>
                    </div>
                  )}
                  {stakeholder.phone && (
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      <span>{stakeholder.phone}</span>
                    </div>
                  )}
                  {stakeholder.city && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{stakeholder.city}, {stakeholder.state}</span>
                    </div>
                  )}
                </div>

                {stakeholder.notes && (
                  <p className="text-xs text-gray-500 mt-3 line-clamp-2">{stakeholder.notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Stakeholder Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add New Stakeholder</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="input"
                  required
                >
                  <option value="parent">Parent</option>
                  <option value="student">Student</option>
                  <option value="alumni">Alumni</option>
                  <option value="partner">Partner</option>
                </select>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
                  <input
                    type="tel"
                    value={formData.alternatePhone}
                    onChange={(e) => setFormData({ ...formData, alternatePhone: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={formData.branchId}
                  onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                  className="input"
                  required
                >
                  <option value="kolkata">Kolkata</option>
                  <option value="dhanbad">Dhanbad</option>
                  <option value="mumbai">Mumbai</option>
                </select>
              </div>

              {/* Type-specific fields */}
              {formData.type === 'student' && (
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Student Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                      <input
                        type="text"
                        value={formData.studentDetails.grade}
                        onChange={(e) => setFormData({
                          ...formData,
                          studentDetails: { ...formData.studentDetails, grade: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                      <input
                        type="text"
                        value={formData.studentDetails.section}
                        onChange={(e) => setFormData({
                          ...formData,
                          studentDetails: { ...formData.studentDetails, section: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Roll Number</label>
                      <input
                        type="text"
                        value={formData.studentDetails.rollNumber}
                        onChange={(e) => setFormData({
                          ...formData,
                          studentDetails: { ...formData.studentDetails, rollNumber: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {formData.type === 'parent' && (
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Parent Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Occupation</label>
                      <input
                        type="text"
                        value={formData.parentDetails.occupation}
                        onChange={(e) => setFormData({
                          ...formData,
                          parentDetails: { ...formData.parentDetails, occupation: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                      <select
                        value={formData.parentDetails.relationship}
                        onChange={(e) => setFormData({
                          ...formData,
                          parentDetails: { ...formData.parentDetails, relationship: e.target.value }
                        })}
                        className="input"
                      >
                        <option value="">Select</option>
                        <option value="father">Father</option>
                        <option value="mother">Mother</option>
                        <option value="guardian">Guardian</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {formData.type === 'alumni' && (
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Alumni Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
                      <input
                        type="text"
                        value={formData.alumniDetails.graduationYear}
                        onChange={(e) => setFormData({
                          ...formData,
                          alumniDetails: { ...formData.alumniDetails, graduationYear: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Occupation</label>
                      <input
                        type="text"
                        value={formData.alumniDetails.currentOccupation}
                        onChange={(e) => setFormData({
                          ...formData,
                          alumniDetails: { ...formData.alumniDetails, currentOccupation: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <input
                        type="text"
                        value={formData.alumniDetails.company}
                        onChange={(e) => setFormData({
                          ...formData,
                          alumniDetails: { ...formData.alumniDetails, company: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {formData.type === 'partner' && (
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Partner Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Organization Type</label>
                      <input
                        type="text"
                        value={formData.partnerDetails.organizationType}
                        onChange={(e) => setFormData({
                          ...formData,
                          partnerDetails: { ...formData.partnerDetails, organizationType: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Partnership Type</label>
                      <input
                        type="text"
                        value={formData.partnerDetails.partnershipType}
                        onChange={(e) => setFormData({
                          ...formData,
                          partnerDetails: { ...formData.partnerDetails, partnershipType: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                      <input
                        type="text"
                        value={formData.partnerDetails.contactPerson}
                        onChange={(e) => setFormData({
                          ...formData,
                          partnerDetails: { ...formData.partnerDetails, contactPerson: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-primary">
                  Create Stakeholder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedStakeholder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold">{selectedStakeholder.name}</h2>
                <span className={`inline-block px-2 py-1 rounded-full text-xs mt-1 ${getTypeBadge(selectedStakeholder.type)}`}>
                  {selectedStakeholder.type}
                </span>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Contact Information */}
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedStakeholder.email && (
                    <div>
                      <p className="text-gray-600">Email</p>
                      <p className="font-medium">{selectedStakeholder.email}</p>
                    </div>
                  )}
                  {selectedStakeholder.phone && (
                    <div>
                      <p className="text-gray-600">Phone</p>
                      <p className="font-medium">{selectedStakeholder.phone}</p>
                    </div>
                  )}
                  {selectedStakeholder.alternatePhone && (
                    <div>
                      <p className="text-gray-600">Alternate Phone</p>
                      <p className="font-medium">{selectedStakeholder.alternatePhone}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Address */}
              {selectedStakeholder.address && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2">Address</h3>
                  <p className="text-sm">{selectedStakeholder.address}</p>
                  <p className="text-sm">{selectedStakeholder.city}, {selectedStakeholder.state} - {selectedStakeholder.pincode}</p>
                </div>
              )}

              {/* Type-specific Details */}
              {selectedStakeholder.studentDetails && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2">Student Details</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Grade</p>
                      <p className="font-medium">{selectedStakeholder.studentDetails.grade}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Section</p>
                      <p className="font-medium">{selectedStakeholder.studentDetails.section}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Roll Number</p>
                      <p className="font-medium">{selectedStakeholder.studentDetails.rollNumber}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedStakeholder.parentDetails && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2">Parent Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Occupation</p>
                      <p className="font-medium">{selectedStakeholder.parentDetails.occupation}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Relationship</p>
                      <p className="font-medium">{selectedStakeholder.parentDetails.relationship}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedStakeholder.alumniDetails && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2">Alumni Details</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Graduation Year</p>
                      <p className="font-medium">{selectedStakeholder.alumniDetails.graduationYear}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Current Occupation</p>
                      <p className="font-medium">{selectedStakeholder.alumniDetails.currentOccupation}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Company</p>
                      <p className="font-medium">{selectedStakeholder.alumniDetails.company}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedStakeholder.partnerDetails && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2">Partner Details</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Organization Type</p>
                      <p className="font-medium">{selectedStakeholder.partnerDetails.organizationType}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Partnership Type</p>
                      <p className="font-medium">{selectedStakeholder.partnerDetails.partnershipType}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Contact Person</p>
                      <p className="font-medium">{selectedStakeholder.partnerDetails.contactPerson}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedStakeholder.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-sm text-gray-600">{selectedStakeholder.notes}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button onClick={() => setShowDetailsModal(false)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakeholderManagement;
